import { useNavigate } from 'react-router-dom';
import { BellOff } from '@peacecover/icon';
import { daysSinceInclusive } from '@pocapan/shared/utils/DateUtil';
import type { ChatRoomType } from '../types/ChatMessageType';

const BettieChatCard = ({ chatRoom }: { chatRoom: ChatRoomType }) => {
  const navigate = useNavigate();
  const unreadCount = getUnreadCount(chatRoom);
  const lastMessageDate = chatRoom.lastMessage?.sentAt ?? chatRoom.createdAt;

  return (
    <button
      type="button"
      onClick={() =>
        navigate(`/bettie-chats/${chatRoom.chatRoomId}`, {
          state: { from: 'bettie-chat-list' }
        })
      }
      key={chatRoom.chatRoomId}
      className="w-full rounded-[20px] bg-pure-white px-[18px] py-[8px] text-left shadow-[0_0_3px_rgba(0,0,0,0.02),_0_0_13px_rgba(0,0,0,0.03)] dark:bg-background-elevated"
    >
      <div className="flex items-center justify-between border-b border-surface-lv1 py-[10px]">
        <p className="text-[12px] font-medium leading-[14px] text-text-main-lv4">
          <span className="font-semibold text-text-main-lv3">
            Day {daysSinceInclusive(chatRoom.createdAt)}{' '}
          </span>
          <span>💕</span>
        </p>
        <p className="text-[11px] font-normal leading-[13px] text-text-sub-lv1">
          Started {formatStartedDate(chatRoom.createdAt)}
        </p>
      </div>
      <div className="flex items-center gap-[10px] py-[12px]">
        <BettieAvatar
          thumbnailUrl={chatRoom.bettieProfile.thumbnailUrl || chatRoom.bettieProfile.pictureUrl}
        />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-[4px]">
          <div className="flex w-full items-center justify-between gap-[8px]">
            <div className="flex min-w-0 items-center gap-[4px]">
              <p className="truncate text-[17px] font-semibold leading-[20px] text-text-main-lv2">
                {chatRoom.bettieProfile.nickname}
              </p>
              <BellOff size={14} className="shrink-0 text-text-sub-lv1" />
            </div>
            <p className="shrink-0 text-[12px] font-normal leading-[14px] text-text-sub-lv1">
              {formatTime(lastMessageDate)}
            </p>
          </div>
          <div className="flex w-full items-center gap-[4px]">
            <p className="min-w-0 flex-1 truncate text-[14px] font-normal leading-[17px] text-text-main-lv3">
              {chatRoom.lastMessage?.content}
            </p>
            {unreadCount > 0 && (
              <span className="flex h-[16px] min-w-[16px] shrink-0 items-center justify-center rounded-[42px] bg-[linear-gradient(90deg,#FF45A9_0%,#FF6A6A_100%)] px-[4px] text-[11px] font-semibold leading-[13px] text-pure-white">
                {formatUnreadCount(unreadCount)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default BettieChatCard;

const BettieAvatar = ({ thumbnailUrl }: { thumbnailUrl?: string | null }) => {
  return (
    <div className="size-[48px] shrink-0 overflow-hidden rounded-full bg-theirty-light dark:bg-theirty-dark">
      <img
        src={thumbnailUrl || '/assets/bettie/bettie_empty_profile.png'}
        alt=""
        className="size-full object-cover"
      />
    </div>
  );
};

const getUnreadCount = (chatRoom: ChatRoomType) => {
  const profileIds = [
    chatRoom.myProfile?.userProfileId,
    chatRoom.myProfile?.userProfileId?.replace(/-/g, '')
  ].filter(Boolean) as string[];

  return (
    chatRoom.lastMessage?.unseenCounts?.reduce((total, countByProfile) => {
      const profileCount = profileIds.reduce((sum, profileId) => {
        return sum + (countByProfile[profileId] ?? 0);
      }, 0);
      return total + profileCount;
    }, 0) ?? 0
  );
};

const formatUnreadCount = (count: number) => {
  if (count > 99) return '99+';
  return String(count);
};

const formatStartedDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  const period = date.getHours() >= 12 ? 'PM' : 'AM';
  const hour = date.getHours() % 12 || 12;
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${period} ${hour}:${minute}`;
};
